import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import type { Scene } from "@babylonjs/core/scene";
import type { GridMap } from "../generation/GridMap";
import { hasWorldLineOfSight } from "../rendering/WorldOcclusion";
import { worldToGrid } from "../rendering/worldCoordinates";

interface Projectile {
  readonly mesh: Mesh;
  readonly velocity: Vector3;
  readonly nextPosition: Vector3;
  remaining: number;
  damage: number;
}

export class ProjectileSystem {
  private readonly projectiles: Projectile[] = [];

  public constructor(
    private readonly scene: Scene,
    private readonly grid: GridMap,
    private readonly damagePlayer: (amount: number) => void,
  ) {
    const material = new StandardMaterial("stalker-projectile-material", scene);
    material.diffuseColor = Color3.Black();
    material.emissiveColor = new Color3(1, 0.12, 0.02);
    for (let index = 0; index < 12; index += 1) {
      const mesh = MeshBuilder.CreateIcoSphere(
        `stalker-projectile-${index}`,
        { radius: 0.22, subdivisions: 1 },
        scene,
      );
      mesh.material = material;
      mesh.setEnabled(false);
      this.projectiles.push({
        mesh,
        velocity: Vector3.Zero(),
        nextPosition: Vector3.Zero(),
        remaining: 0,
        damage: 0,
      });
    }
  }

  public spawn(origin: Vector3, target: Vector3, damage: number): void {
    const projectile = this.projectiles.find((candidate) => candidate.remaining <= 0);
    if (!projectile) return;
    projectile.mesh.position.copyFrom(origin);
    const offsetX = target.x - origin.x;
    const offsetY = target.y - origin.y;
    const offsetZ = target.z - origin.z;
    const distance = Math.hypot(offsetX, offsetY, offsetZ) || 1;
    projectile.velocity.set(
      (offsetX / distance) * 5.2,
      (offsetY / distance) * 5.2,
      (offsetZ / distance) * 5.2,
    );
    projectile.remaining = 5;
    projectile.damage = damage;
    projectile.mesh.setEnabled(true);
  }

  public update(deltaSeconds: number, playerPosition: Vector3): void {
    for (const projectile of this.projectiles) {
      if (projectile.remaining <= 0) continue;
      projectile.remaining -= deltaSeconds;
      const nextPosition = projectile.nextPosition;
      nextPosition.set(
        projectile.mesh.position.x + projectile.velocity.x * deltaSeconds,
        projectile.mesh.position.y + projectile.velocity.y * deltaSeconds,
        projectile.mesh.position.z + projectile.velocity.z * deltaSeconds,
      );
      if (!hasWorldLineOfSight(this.scene, projectile.mesh.position, nextPosition)) {
        this.disable(projectile);
        continue;
      }
      projectile.mesh.position.copyFrom(nextPosition);
      if (
        !this.grid.isWalkable(
          worldToGrid(projectile.mesh.position.x, projectile.mesh.position.z, this.grid),
          false,
        )
      ) {
        this.disable(projectile);
        continue;
      }
      if (Vector3.DistanceSquared(projectile.mesh.position, playerPosition) < 0.7) {
        this.damagePlayer(projectile.damage);
        this.disable(projectile);
      } else if (projectile.remaining <= 0) {
        this.disable(projectile);
      }
    }
  }

  private disable(projectile: Projectile): void {
    projectile.remaining = 0;
    projectile.mesh.setEnabled(false);
  }
}
